import React, { useState, useCallback, useRef, useEffect } from 'react';
import { PRODUCTS_API, request } from '../services/api';
const AdminContext = React.createContext();
const AdminIsLoggedProvider = ({ children }) => {
  const [logged, setLogged] = useState(false);
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState('');
  const boot = useRef(null);
  const unsubscribe = useRef(null);
  const verification = useRef(0);
  const authorize = useCallback(async (candidate) => {
    const attempt = ++verification.current;
    setChecking(true);
    try {
      if (!candidate) { setLogged(false); setUser(null); return false; }
      const token = await candidate.getIdToken();
      const session = await request(`${PRODUCTS_API}/admin/session`, { headers: { Authorization: `Bearer ${token}` } });
      if (attempt !== verification.current) return false;
      setLogged(true); setUser(session); setAuthError(''); return true;
    } catch {
      if (attempt === verification.current) {
        setLogged(false); setUser(null);
        setAuthError('Esta cuenta no tiene acceso de administrador, su correo no está verificado o el servidor no está disponible.');
      }
      return false;
    } finally { if (attempt === verification.current) setChecking(false); }
  }, []);
  const ensureSession = useCallback(() => {
    if (!boot.current) boot.current = import('../services/auth').then(module => new Promise(resolve => {
      unsubscribe.current = module.onIdTokenChanged(module.auth, async candidate => { await authorize(candidate); resolve(); });
    })).catch(() => { setChecking(false); setAuthError('No se pudo conectar con el servicio de acceso.'); });
    return boot.current;
  }, [authorize]);
  const login = async (email, password) => {
    await ensureSession();
    const module = await import('../services/auth');
    const credential = await module.signInWithEmailAndPassword(module.auth, email.trim(), password);
    const allowed = await authorize(credential.user);
    if (!allowed) { await module.signOut(module.auth); throw new Error('No se pudo autorizar esta cuenta. Verificá tu correo y que el servidor esté iniciado.'); }
  };
  const logout = async () => {
    const module = await import('../services/auth');
    await module.signOut(module.auth); setUser(null); setLogged(false); setAuthError('');
  };
  useEffect(() => () => unsubscribe.current?.(), []);
  return <AdminContext.Provider value={{ logged, checking, user, authError, ensureSession, login, logout }}>{children}</AdminContext.Provider>;
};
export { AdminContext, AdminIsLoggedProvider };
